/**
 * An answer's table (the Ask page sketch's "Vastuse tabel A"): the row's name, then what to do,
 * then plain figures, then why, as the backend ordered the columns (lib/ask/answer.js). When it
 * has both what to do and why, the two get a header each and a rule between them, and the row's
 * name columns share one cell (the outlet over its town).
 *
 * The visual formats draw their cell's parts, all of them the query's own figures: from_to as
 * 19 → 24, change as a +5 pill, range as the value on its interval with the mark (the plan's
 * copies on the demand), count as dots (2 of 7 months). Each also reads as text (aria-label).
 * Grouped, the headers wrap, the cells are tight and the outlet's name keeps to one line, so the
 * table fits the card beside the orb (which narrows for it once answered, ask.css).
 */
import { useTranslation } from 'react-i18next'
import { cx } from '@/kit/cx'
import type { AskAnswer, AskCell, AskColumn, AskValue } from './askApi'
import type { makeAskFormats } from './format'

type Table = NonNullable<AskAnswer['table']>
type Formats = ReturnType<typeof makeAskFormats>

// Whole class names, so Tailwind finds them.
const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' } as const
const num = (v: AskValue | undefined): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)
const isRange = (c: AskCell | undefined): c is { value: AskValue; low: AskValue; high: AskValue; mark?: AskValue } => typeof c === 'object' && c !== null && 'low' in c
const isFromTo = (c: AskCell | undefined): c is { from: AskValue; to: AskValue } => typeof c === 'object' && c !== null && 'from' in c
const isCount = (c: AskCell | undefined): c is { value: AskValue; of: AskValue } => typeof c === 'object' && c !== null && 'of' in c

/** A range column's scale: from zero (or its lowest, below zero) to a little past its highest end or mark. */
function scaleOf(rows: AskCell[][], i: number): { lo: number; hi: number } {
    let lo = 0
    let hi = 0
    for (const row of rows) {
        const c = row[i]
        if (!isRange(c)) continue
        for (const v of [c.value, c.low, c.high, c.mark]) {
            const n = num(v)
            if (n !== null) {
                lo = Math.min(lo, n)
                hi = Math.max(hi, n)
            }
        }
    }
    return hi > lo ? { lo, hi: hi + (hi - lo) * 0.06 } : { lo, hi: lo + 1 }
}

function align(column: AskColumn, rows: AskCell[][], i: number, fmt: Formats): 'left' | 'right' | 'center' {
    if (column.format === 'range' || column.format === 'count') return 'left'
    if (rows.some((r) => typeof r[i] === 'boolean')) return 'center'
    return fmt.isNumeric(column.format) || rows.some((r) => typeof r[i] === 'number') ? 'right' : 'left'
}

function Change({ v, fmt }: { v: AskValue; fmt: Formats }) {
    const n = num(v)
    if (n === null || n === 0) return <span className="text-[var(--text-weaker)]">{fmt.cell(v, 'change')}</span>
    return (
        <span className={cx('inline-block min-w-[30px] rounded-full px-[8px] py-[1px] text-center font-[700]', n > 0 ? 'bg-[var(--accent-weak)] text-[var(--accent-default)]' : 'bg-[var(--surface-header)] text-[var(--text-default)]')}>
            {fmt.cell(v, 'change')}
        </span>
    )
}

function Range({ c, scale, fmt }: { c: { value: AskValue; low: AskValue; high: AskValue; mark?: AskValue }; scale: { lo: number; hi: number }; fmt: Formats }) {
    const at = (v: AskValue | undefined) => {
        const n = num(v)
        return n === null ? null : ((n - scale.lo) / (scale.hi - scale.lo)) * 100
    }
    const [low, high, value, mark] = [at(c.low), at(c.high), at(c.value), at(c.mark)]
    return (
        <div className="flex flex-col">
            <div className="flex items-center gap-[8px]">
                <span className="min-w-[28px] text-right whitespace-nowrap">{fmt.estimate(c.value)}</span>
                <span role="img" aria-label={fmt.cell(c, 'range')} className="relative inline-block h-[10px] w-[64px] shrink-0">
                    <span className="absolute top-[4px] right-0 left-0 h-[2px] rounded-[1px] bg-[var(--border-default)]" />
                    {low !== null && high !== null && <span className="absolute top-[2px] h-[6px] rounded-[3px] bg-[var(--border-strong)]" style={{ left: `${low}%`, width: `${Math.max(1, high - low)}%` }} />}
                    {value !== null && <span className="absolute top-[1px] ml-[-4px] size-[8px] rounded-full bg-[var(--text-weak)]" style={{ left: `${value}%` }} />}
                    {mark !== null && <span className="absolute top-[-1px] ml-[-1px] h-[12px] w-[2px] rounded-[1px] bg-[var(--accent-default)]" style={{ left: `${mark}%` }} />}
                </span>
            </div>
            <span className="pl-[36px] text-[11.5px] leading-[15px] whitespace-nowrap text-[var(--text-weaker)]">
                {fmt.cell(c.low, null)}–{fmt.cell(c.high, null)}
            </span>
        </div>
    )
}

function Count({ c, fmt }: { c: { value: AskValue; of: AskValue }; fmt: Formats }) {
    const n = num(c.value)
    const of = num(c.of)
    const dots = n !== null && of !== null && Number.isInteger(of) && of > 0 && of <= 12 ? Array.from({ length: of }, (_, i) => i < n) : null
    return (
        <span className="inline-flex flex-col gap-[3px] whitespace-nowrap">
            {dots && (
                <span role="img" aria-label={fmt.cell(c, 'count')} className="inline-flex gap-[2px]">
                    {dots.map((on, i) => (
                        <span key={i} className={cx('size-[6px] rounded-full', on ? 'bg-[var(--text-weak)]' : 'bg-[var(--border-strong)]')} />
                    ))}
                </span>
            )}
            <span className={cx('text-[var(--text-weak)]', dots && 'text-[11.5px] leading-[15px]')} aria-hidden={dots ? 'true' : undefined}>
                {fmt.cell(c, 'count')}
            </span>
        </span>
    )
}

function Cell({ v, column, scale, fmt }: { v: AskCell; column: AskColumn; scale: { lo: number; hi: number } | null; fmt: Formats }) {
    if (column.format === 'from_to' && isFromTo(v)) {
        return (
            <span className="whitespace-nowrap">
                <span className="text-[var(--text-weaker)]">{fmt.cell(v.from, null)}</span>
                <span className="mx-[4px] text-[var(--text-weaker)]">→</span>
                <span className="text-[13.5px] font-[700]">{fmt.cell(v.to, null)}</span>
            </span>
        )
    }
    if (column.format === 'change' && !isFromTo(v)) return <Change v={v as AskValue} fmt={fmt} />
    if (column.format === 'range' && isRange(v) && scale) return <Range c={v} scale={scale} fmt={fmt} />
    if (column.format === 'count' && isCount(v)) return <Count c={v} fmt={fmt} />
    return <>{fmt.cell(v, column.format)}</>
}

export function AnswerTable({ table, fmt }: { table: Table; fmt: Formats }) {
    const { t } = useTranslation()
    const columns = table.columns
    const grouped = columns.some((c) => c.group === 'action') && columns.some((c) => c.group === 'reason')
    const firstReason = grouped ? columns.findIndex((c) => c.group === 'reason') : -1
    // Grouped, the row's names share the first cell: the outlet over its town.
    const names = grouped ? columns.flatMap((c, i) => (c.group === 'name' ? [i] : [])) : []
    const merged = names.length > 1 && names.every((i, k) => i === k) ? names : []
    const shown = columns.map((_, i) => i).filter((i) => !merged.includes(i) || i === merged[0])
    const scales = columns.map((c, i) => (c.format === 'range' ? scaleOf(table.rows, i) : null))
    const aligns = columns.map((c, i) => align(c, table.rows, i, fmt))
    const rule = (i: number) => i === firstReason && 'border-l border-solid border-[var(--border-default)] pl-[12px]'

    return (
        <table className="w-full border-collapse text-[12.5px] leading-[18px] tabular-nums">
            <thead>
                {grouped && (
                    <tr className="bg-[var(--surface-header)]">
                        <th scope="colgroup" colSpan={shown.filter((i) => i < firstReason).length} className="px-[8px] pt-[8px] pb-0 text-left text-[11px] leading-[14px] font-[650] tracking-[0.06em] text-[var(--accent-default)] uppercase">
                            {t('ask.group.action')}
                        </th>
                        <th scope="colgroup" colSpan={shown.filter((i) => i >= firstReason).length} className="border-l border-solid border-[var(--border-default)] pt-[8px] pr-[8px] pb-0 pl-[12px] text-left text-[11px] leading-[14px] font-[650] tracking-[0.06em] text-[var(--text-weaker)] uppercase">
                            {t('ask.group.reason')}
                        </th>
                    </tr>
                )}
                <tr className="bg-[var(--surface-header)] text-[var(--text-weak)]">
                    {shown.map((i) => (
                        <th key={columns[i]!.key} scope="col" className={cx('py-[7px] align-bottom font-[600]', grouped ? 'px-[8px] pt-[2px]' : 'px-[10px] whitespace-nowrap', ALIGN[aligns[i]!], rule(i))}>
                            {i === merged[0] ? merged.map((m) => columns[m]!.label).join(' · ') : columns[i]!.label}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {table.rows.map((row, r) => (
                    <tr key={r} className="border-t border-solid border-[var(--border-default)]">
                        {shown.map((i) => (
                            <td key={i} className={cx('py-[7px] align-middle font-normal text-[var(--text-default)]', grouped ? 'px-[8px]' : 'px-[10px]', ALIGN[aligns[i]!], aligns[i] === 'right' && 'whitespace-nowrap', rule(i))}>
                                {i === merged[0] ? (
                                    <>
                                        <div className="font-[600] whitespace-nowrap">{fmt.cell(row[i], columns[i]!.format)}</div>
                                        {merged.slice(1).map((m) => (
                                            <div key={m} className="text-[11.5px] leading-[15px] text-[var(--text-weaker)]">
                                                {fmt.cell(row[m], columns[m]!.format)}
                                            </div>
                                        ))}
                                    </>
                                ) : (
                                    <Cell v={row[i] ?? null} column={columns[i]!} scale={scales[i] ?? null} fmt={fmt} />
                                )}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    )
}
